import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Avatar, Typography, CircularProgress, Grid, Tooltip, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Business, LocalHospital, School, Store, Science, CardTravel,
  HomeWork, AttachMoney, AccountBalance, Gavel, Computer,
} from '@mui/icons-material';

const industries = [
  { label: 'IT', icon: <Computer />, value: 'it', color: '#546e7a' },
  { label: 'Insurance', icon: <Business />, value: 'insurance', color: '#1976d2' },
  { label: 'Healthcare', icon: <LocalHospital />, value: 'healthcare', color: '#e53935' },
  { label: 'Education', icon: <School />, value: 'education', color: '#8e24aa' },
  { label: 'Research', icon: <Science />, value: 'research', color: '#43a047' },
  { label: 'Travel', icon: <CardTravel />, value: 'travel', color: '#00acc1' },
  { label: 'Real Estate', icon: <HomeWork />, value: 'real-estate', color: '#6d4c41' },
  { label: 'Finance', icon: <AttachMoney />, value: 'finance', color: '#2e7d32' },
  { label: 'Banking', icon: <AccountBalance />, value: 'banking', color: '#3949ab' },
  { label: 'Legal', icon: <Gavel />, value: 'legal', color: '#5e35b1' },
  { label: 'Small Industries', icon: <Store />, value: 'small-industries', color: '#f57c00' },
  
];

const Companies = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const industryParam = queryParams.get('industry');

  const [selectedIndustry, setSelectedIndustry] = useState(industryParam || 'it');
  
  const [rolesData, setRolesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    fetchRolesData(selectedIndustry);
  }, [selectedIndustry]);

  const fetchRolesData = async (industry) => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/companies?industry=${industry}`);
      const groupedRoles = groupRolesByCompany(response.data);
      setRolesData(groupedRoles);
    } catch (error) {
      console.error('Error fetching roles data', error);
      setRolesData([]);
    } finally {
      setLoading(false);
    }
  };

  const groupRolesByCompany = (data) => {
    const grouped = {};

    data.forEach((item) => {
      item.roles.forEach((role) => {
        if (!grouped[item.role]) {
          grouped[item.role] = [];
        }
        const existingCompany = grouped[item.role].find((company) => company.companyname === role.companyname);

        if (existingCompany) {
          existingCompany.employeeCount += role.employeeCount;
        } else {
          grouped[item.role].push({ companyname: role.companyname, employeeCount: role.employeeCount });
        }
      });
    });

    return grouped;
  };

  return (
    <Box sx={{ maxWidth: 1300, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom textAlign="center">
        Explore Companies by Industry
      </Typography>

      <Box
  sx={{
    display: 'flex',
    overflowX: 'auto',
    gap: 2,
    mb: 4,
    pb: 1,
    '&::-webkit-scrollbar': { display: 'none' },
    flexWrap: 'nowrap',
    justifyContent: 'flex-start', // Align items to the left for mobile
    width: '100%',
  }}
>
  {industries.map((industry) => (
    <motion.div
      key={industry.value}
      whileHover={{ scale: 1.07 }}
      whileTap={{ scale: 0.95 }}
      transition={{ duration: 0.3 }}
    >
      <Tooltip title={industry.label}>
        <Avatar
          onClick={() => setSelectedIndustry(industry.value)}
          sx={{
            width: isMobile ? 60 : 80, // Adjust avatar size for mobile
            height: isMobile ? 60 : 80, // Adjust avatar size for mobile
            cursor: 'pointer',
            backgroundColor: selectedIndustry === industry.value ? industry.color : '#f5f5f5',
            color: selectedIndustry === industry.value ? '#fff' : '#333',
            display: 'flex',
            justifyContent: 'center', // Ensure horizontal centering
            alignItems: 'center', // Ensure vertical centering
            boxShadow: selectedIndustry === industry.value ? 4 : 2,
            transition: 'all 0.3s ease-in-out',
          }}
        >
          {industry.icon}
        </Avatar>
      </Tooltip>
      <Typography
        variant="body2"
        fontWeight="600"
        textAlign="center"
        sx={{
          marginTop: 1,
          color: selectedIndustry === industry.value ? '#fff' : '#333',
          fontSize: isMobile ? '0.75rem' : '1rem',
          textShadow: selectedIndustry === industry.value ? '2px 2px 4px rgba(0, 0, 0, 0.7)' : 'none',
        }}
      >
        {industry.label}
      </Typography>
    </motion.div>
  ))}
</Box>


      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
          <CircularProgress size={50} color="primary" />
        </Box>
      ) : Object.keys(rolesData).length > 0 ? (
        <Grid container spacing={3}>
          {Object.keys(rolesData).map((role, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <Box
                  sx={{
                    p: 3,
                    borderRadius: 3,
                    textAlign: 'center',
                    background: '#ffffff',
                    boxShadow: 4,
                    transition: 'transform 0.3s',
                    '&:hover': {
                      transform: 'scale(1.04)',
                      boxShadow: 6,
                    },
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {role}
                  </Typography>
                  {rolesData[role].map((company, idx) => (
                    <Typography key={idx} variant="body2" color="text.secondary">
                      {company.companyname}: {company.employeeCount}
                    </Typography>
                  ))}
                </Box>
              </motion.div>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box textAlign="center" mt={8}>
          <Typography variant="h6" color="text.secondary">
            No companies found for this industry.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Companies;
